import { useEffect } from 'react'
import { lazyWithRetry } from "./common/tools/lazyWithRetry";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import "./App.scss";
import 'react-toastify/dist/ReactToastify.css';
import { Layout as Public, Layout as Private } from './common/components/Layout';
import { SUPPORT, SUBSCRIPTION, MIGRATIS, GENERATOR } from './settings';

const Home = lazyWithRetry(() => import('./common/components/Home'));
const Message = lazyWithRetry(() => import('./common/components/Message'));
const Cookies = lazyWithRetry(() => import('./common/components/Cookies'));
const Register = lazyWithRetry(() => import('./user/components/Register'));
const Invitation = lazyWithRetry(() => import('./user/components/Invitation'));
const Reset = lazyWithRetry(() => import('./user/components/Reset'));
const Password = lazyWithRetry(() => import('./user/components/Password'));
const AccountSettings = lazyWithRetry(() => import('./user/components/account/AccountSettings'));
const Subscribe = lazyWithRetry(() => import('./subscription/components/Subscribe'));
const Contact = lazyWithRetry(() => import('./support/components/Contact'));
const Tickets = lazyWithRetry(() => import('./support/components/Tickets'));
const Disclaimer = lazyWithRetry(() => import('./common/components/Disclaimer'));
const LegalNotice = lazyWithRetry(() => import('./common/components/LegalNotice'));
const RefundPolicy = lazyWithRetry(() => import('./common/components/RefundPolicy'));
const Licensing = lazyWithRetry(() => import('./common/components/Licensing'));
const Security = lazyWithRetry(() => import('./common/components/Security'));
const Status = lazyWithRetry(() => import('./common/components/Status'));
const Help = lazyWithRetry(() => import('./common/components/Help'));
const LlmProviders = lazyWithRetry(() => import('./common/components/LlmProviders'));
const Subitem = lazyWithRetry(() => import('./migratis/components/Subitem'));
const Item = lazyWithRetry(() => import('./migratis/components/Item'));
const GeneratorApplication = lazyWithRetry(() => import('./generator/components/Application'));
const GeneratorEntity      = lazyWithRetry(() => import('./generator/components/Entity'));
const GeneratorField       = lazyWithRetry(() => import('./generator/components/Field'));
const GeneratorWorkflow    = lazyWithRetry(() => import('./generator/components/Workflow'));
const GeneratorPages       = lazyWithRetry(() => import('./generator/components/pages/Pages'));
const GeneratorSandbox     = lazyWithRetry(() => import('./generator/components/sandbox/Sandbox'));
const GeneratorSimulator   = lazyWithRetry(() => import('./generator/components/sandbox/SimulatorPage'));
const GeneratorDocs        = lazyWithRetry(() => import('./generator/components/GeneratorDocs'));
const CustomComponents = lazyWithRetry(() => import('./generator/components/CustomComponents'));
const DataSources = lazyWithRetry(() => import('./generator/components/DataSources'));
const CalculatedTables = lazyWithRetry(() => import('./generator/components/derivations/CalculatedTables'));
const GeneratorRoles       = lazyWithRetry(() => import('./generator/components/Roles'));

const App = () => {

  const location = useLocation();
  useEffect(() => {

    if (window.location.hostname.includes("dev.migratis")) {
      const meta = document.createElement("meta");
      meta.name = "robots";
      meta.content = "noindex";
      document.head.appendChild(meta);
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behaviour: "smooth"
    });

  }, [location.pathname]);

  return (
    <>
      <Routes>
        {/* Sandbox — completely bare, no layout chrome, token is the access credential */}
        { GENERATOR && <Route exact path="/sandbox/:token" element={<GeneratorSandbox />} /> }
        { GENERATOR && <Route exact path="/sandbox/:token/simulate/:stepId" element={<GeneratorSimulator />} /> }

        <Route element={<Public private={false}/>}>
          <Route exact path="/home" element={<Home />} />
          <Route exact path="/Message" element={<Message />} />         
          <Route exact path="/cookies" element={<Cookies />} />
          <Route exact path="/register" element={<Register />} />
          <Route exact path="/invitation" element={<Invitation />} />            
          <Route exact path="/reset" element={<Reset />} />  
          <Route exact path="/password" element={<Password />} />
          <Route exact path="/contact" element={<Contact />} />  
          {/* Legal & information pages — all public, all linked from the footer */}
          <Route exact path="/disclaimer" element={<Disclaimer />} />
          <Route exact path="/legal-notice" element={<LegalNotice />} />
          <Route exact path="/refund-policy" element={<RefundPolicy />} />
          { GENERATOR && <Route exact path="/licensing" element={<Licensing />} /> }
          <Route exact path="/security" element={<Security />} />
          <Route exact path="/status" element={<Status />} />
          <Route exact path="/help" element={<Help />} />
          { GENERATOR && <Route exact path="/llm-providers" element={<LlmProviders />} /> }
          <Route path={"/"} element={<Home />} />
        </Route>
        <Route element={<Private private={true}/>}>
          <Route exact path='/account' element={<AccountSettings/>} />
          {/* /profile kept as a redirect for back-compat — account hub is /account */}
          <Route exact path='/profile' element={<Navigate to="/account" replace />} />
          { SUPPORT &&
            <>
              <Route exact path="/support/ticket" element={<Tickets />} />             
            </>
          }
          { SUBSCRIPTION &&
            <>
              <Route exact path='/subscribe' element={<Subscribe/>} />            
            </> 
          }           
          { MIGRATIS &&
            <>
              <Route exact path='/migratis/subitem' element={<Subitem/>} />
              <Route exact path='/migratis/item' element={<Item/>} />            
            </>        
          }
          { GENERATOR &&
            <>
              <Route exact path='/generator/application' element={<GeneratorApplication/>} />
              <Route exact path='/generator/entity'      element={<GeneratorEntity/>} />
              <Route exact path='/generator/field'       element={<GeneratorField/>} />
              <Route exact path='/generator/workflow'    element={<GeneratorWorkflow/>} />
              <Route exact path='/generator/pages'       element={<GeneratorPages/>} />
              <Route exact path='/generator/docs'        element={<GeneratorDocs/>} />
              <Route exact path='/generator/components' element={<CustomComponents/>} />
              <Route exact path='/generator/data-sources' element={<DataSources/>} />
              <Route exact path='/generator/calculated-tables' element={<CalculatedTables/>} />
              <Route exact path='/generator/roles'      element={<GeneratorRoles/>} />
            </>
          }     
        </Route>
      </Routes>
    </>
  );
};

export default App;
